import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import FormItem from '../FormComponents/FormItem';
import { personalizationDataShape } from '../formPropTypes';
import { useConfigStore } from '../../../../stores';

const AccountFoundStep = ({
  handleChange,
  handleBlur,
  values,
  errors,
  touched,
  isSubmitting,
  crmDataState,
  dataIsSent,
  setCurrentStep,
  footerNav,
}) => {
  const crmConfig = useConfigStore((state) => state.crmConfig);
  const {
    humanName,
    machineName,
    loginUrl,
  } = crmConfig;

  // A person who doesn't exist in the CRM yet: we're creating them, so every
  // field is ours to fill in even on a CRM whose existing records we can't edit.
  const isNewProspect = crmDataState === 'notFound';

  // Sherpa accepts nothing but a sales note on a prospect it already holds — no
  // name, no email, no spouse attached after the fact — so blanks can be filled
  // in for YourTour's own record but none of it travels back.
  const SherpaInstructions = () => (isNewProspect
    ? `Enter the details for this new ${humanName} prospect. `
    : `Empty ${humanName} values can be filled in for YourTour. Existing ${humanName} values cannot be edited, and nothing entered here is sent to ${humanName}. `);

  // Form instructions for Welcome Home or other CRMs that allow changes.
  const EditableCrmInstructions = () => `We found a match in ${humanName}. Review and/or update the information below. Any Person 2 additons will automatically be sent to ${humanName}.`;
  const AlineInstructions = () => (crmDataState === 'found' ? 'Empty Aline values can be updated. Existing Aline values cannot be edited.' : 'Review and/or update the information below. ');

  // Form instructions for people that are just using Drupal as their CRM.
  const DrupalInstructions = () => 'Review and/or update the prospect\'s information below. ';

  // Select instructions based on CRM.
  const Instructions = () => {
    let instructions = null;
    switch (machineName) {
      case 'welcomeHome':
        instructions = <EditableCrmInstructions />;
        break;
      case 'enquire':
        instructions = <AlineInstructions />;
        break;
      case 'sherpa':
        instructions = <SherpaInstructions />;
        break;
      default:
        instructions = <DrupalInstructions />;
    }
    return instructions;
  };

  // When Enquire or Sherpa returns an existing match we can't overwrite the
  // details it already holds, but we can fill in any it left blank — Enquire
  // takes those fill-ins, Sherpa keeps them local to YourTour. Only lock on a
  // real match ('found') — for a new person the fields hold the lookup entry,
  // not CRM data, so they stay editable. Snapshot which arrived populated
  // (captured once on mount) so typing into an initially-empty field doesn't
  // lock it mid-edit.
  const crmMatch = ['enquire', 'sherpa'].includes(machineName) && crmDataState === 'found';
  const [crmLockedFields] = useState(() => ({
    first_name: crmMatch && Boolean(values?.first_name),
    last_name: crmMatch && Boolean(values?.last_name),
    email: crmMatch && Boolean(values?.email),
    person2_first_name: crmMatch && Boolean(values?.person2_first_name),
    person2_last_name: crmMatch && Boolean(values?.person2_last_name),
    person2_email: crmMatch && Boolean(values?.person2_email),
  }));

  // Per-field disabled state for Person 1, so we can focus the first editable one.
  const firstNameDisabled = crmLockedFields.first_name;
  const lastNameDisabled = crmLockedFields.last_name;
  const emailDisabled = crmLockedFields.email;

  // Person 2 fields that arrived populated from a CRM match can't be
  // overwritten.
  const person2FirstNameDisabled = crmLockedFields.person2_first_name;
  const person2LastNameDisabled = crmLockedFields.person2_last_name;
  const person2EmailDisabled = crmLockedFields.person2_email;

  // "Edit in <CRM>" only makes sense for someone who already exists there. A
  // new person has no CRM record to open yet.
  const showCrmEditLink = machineName !== 'drupal' && crmDataState !== 'notFound';

  return (
    <div data-testid="personalization-step:edit">
      <div className="modal__section">
        <h1 className="modal__subsection modal__title modal__title--short">
          Personalize YourTour
        </h1>
        <div className="modal__subsection modal__description modal__description--short">
          <Instructions />
          Click START PERSONALIZATION to proceed.
        </div>
      </div>

      <div className="modal__section">
        <div className="modal__subheader modal__group">
          Person 1
        </div>
        <div className="form__items modal__group">
          <div className="form__items__row">
            <FormItem
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values?.first_name}
              touched={touched}
              errors={errors}
              type="text"
              name="first_name"
              placeholder="First Name"
              disabled={firstNameDisabled}
              autoFocus={!firstNameDisabled}
            />

            <FormItem
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values?.last_name}
              touched={touched}
              errors={errors}
              type="text"
              name="last_name"
              placeholder="Last Name"
              disabled={lastNameDisabled}
              autoFocus={firstNameDisabled && !lastNameDisabled}
            />
          </div>

          <FormItem
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values?.email}
            touched={touched}
            errors={errors}
            type="email"
            name="email"
            placeholder="Email"
            disabled={emailDisabled}
            autoFocus={firstNameDisabled && lastNameDisabled && !emailDisabled}
          />
        </div>
      </div>

      <div className="modal__section">
        <div className="modal__subheader modal__group">
          Person 2
        </div>
        <div className="form__items modal__group">
          <div className="form__items__row">
            <FormItem
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values?.person2_first_name}
              touched={touched}
              errors={errors}
              type="text"
              name="person2_first_name"
              placeholder="Person 2 First Name"
              disabled={person2FirstNameDisabled}
            />

            <FormItem
              handleChange={handleChange}
              handleBlur={handleBlur}
              values={values?.person2_last_name}
              touched={touched}
              errors={errors}
              type="text"
              name="person2_last_name"
              placeholder="Person 2 Last Name"
              disabled={person2LastNameDisabled}
            />
          </div>

          <FormItem
            handleChange={handleChange}
            handleBlur={handleBlur}
            values={values?.person2_email}
            touched={touched}
            errors={errors}
            type="email"
            name="person2_email"
            placeholder="Person 2 Email"
            disabled={person2EmailDisabled}
          />
        </div>
      </div>

      <div className="modal__section modal__btns modal__btns--inline">
        <button
          disabled={dataIsSent || isSubmitting}
          type="submit"
          className={classNames('modal__btn modal__btn--secondary', {
            'modal__btn--working': dataIsSent || isSubmitting,
          })}
          data-testid="personalization-start"
        >
          { dataIsSent || isSubmitting ? 'Updating Prospect . . .' : 'Start Personalization'}
        </button>

        { showCrmEditLink && (
          <a
            href={loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="modal__textBtn modal__textBtn--secondary"
            onClick={() => {
              setCurrentStep(1);
            }}
          >
            Or Edit in
            {' '}
            {humanName}
          </a>
        )}
      </div>

      {footerNav}
    </div>
  );
};

AccountFoundStep.propTypes = {
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  values: personalizationDataShape.isRequired,
  errors: PropTypes.shape({}).isRequired,
  touched: PropTypes.shape({}).isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  crmDataState: PropTypes.string.isRequired,
  dataIsSent: PropTypes.bool.isRequired,
  setCurrentStep: PropTypes.func.isRequired,
  footerNav: PropTypes.node,
};

AccountFoundStep.defaultProps = {
  footerNav: null,
};

export default AccountFoundStep;
